import { InternalApiCustomListSchemaField, InternalApiCustomListSchemaFieldType } from '../../api-internal';
import React, { useMemo } from 'react';
import { useParams } from 'react-router';
import useCustomLists from '../useCustomLists';
import { SalvTable, Size } from '@salvapp/ui-components';
import { createColumnHelper } from '@tanstack/react-table';

type Props = {
  fieldCode: string | undefined;
  fieldPathParts: string[];
  additionalSchemaFields: InternalApiCustomListSchemaField[];
};

const MonitoringFieldPreview = ({ fieldCode, fieldPathParts, additionalSchemaFields }: Props) => {
  const { customListId } = useParams();
  const customList = useCustomLists().find((list) => list.id === customListId);

  const data = useMemo(
    () => [
      {
        listCode: `#LIST-${customList?.code}`,
        fieldCode: fieldCode && `#LIST-${customList?.code}-${fieldCode}`,
        value: fieldPathParts.map((pathPart) => 'value_of_' + pathPart).join(' '),
        additionalData:
          additionalSchemaFields.length > 0 &&
          '{\n' +
            additionalSchemaFields
              .map((schemaField) => {
                let value = '';
                if (schemaField.isMulti && schemaField.type === InternalApiCustomListSchemaFieldType.OBJECT) {
                  value = '[{ "key" : "value" }]';
                } else if (schemaField.type === InternalApiCustomListSchemaFieldType.OBJECT) {
                  value = '{ "key" : "value" }';
                } else if (schemaField.isMulti) {
                  value = '[ "value" ]';
                } else {
                  value = '"value"';
                }
                return `  "${schemaField.fieldName}": ${value}`;
              })
              .join(',\n') +
            '\n}',
      },
    ],
    [customList, fieldCode, fieldPathParts, additionalSchemaFields]
  );

  const columnHelper = createColumnHelper<typeof data extends (infer U)[] ? U : never>();

  const columns = useMemo(
    () => [
      columnHelper.accessor('listCode', {
        header: 'list_code',
        cell: ({ getValue }) => <span className={'noTextTransform'}>{getValue()}</span>,
      }),
      columnHelper.accessor('fieldCode', {
        header: 'field_code',
        cell: ({ getValue }) => <span className={'noTextTransform'}>{getValue()}</span>,
      }),
      columnHelper.accessor('value', {
        header: 'value',
      }),
      columnHelper.accessor('additionalData', {
        header: 'additional_data',
        cell: ({ getValue }) => <span className={'multiline'}>{getValue()}</span>,
      }),
    ],
    []
  );

  return (
    <SalvTable
      title={'Preview'}
      data={data}
      columns={columns}
      size={Size.SMALL}
      className={'monitoring-field-preview'}
    />
  );
};

export default MonitoringFieldPreview;
